const CHALLENGE_ID_KEY = "otp_challenge_id";
const CORRELATION_ID_KEY = "otp_correlation_id";
const COOLDOWN_END_KEY = "otp_cooldown_end";
const OTP_REQUIRED_KEY = "otp_required";
export const OTP_SESSION_CHANNEL = "mantle-pp-otp-context";

export type OtpSessionMessage =
    | {
          type: "otp-challenge-set";
          userId: string;
          challengeId: string;
          correlationId: string;
          cooldownEnd: number;
      }
    | { type: "otp-session-cleared"; userId: string };

const canUseStorage =
    typeof window !== "undefined" &&
    typeof window.sessionStorage !== "undefined";

const canUseBroadcastChannel =
    typeof window !== "undefined" && "BroadcastChannel" in window;

let channel: BroadcastChannel | null = null;

function ensureChannel() {
    if (!canUseBroadcastChannel || channel) {
        return;
    }
    channel = new BroadcastChannel(OTP_SESSION_CHANNEL);
}

export function getOtpChallengeId(userId: string): string | null {
    if (!canUseStorage) {
        return null;
    }
    return window.sessionStorage.getItem(`${CHALLENGE_ID_KEY}:${userId}`);
}

export function getOtpCorrelationId(userId: string): string | null {
    if (!canUseStorage) {
        return null;
    }
    return window.sessionStorage.getItem(`${CORRELATION_ID_KEY}:${userId}`);
}

export function getOtpCooldownEnd(userId: string): number {
    if (!canUseStorage) {
        return 0;
    }
    return Number(
        window.sessionStorage.getItem(`${COOLDOWN_END_KEY}:${userId}`) ?? "0"
    );
}

export function isOtpRequired(userId: string): boolean {
    if (!canUseStorage) {
        return false;
    }
    return (
        window.sessionStorage.getItem(`${OTP_REQUIRED_KEY}:${userId}`) === "1"
    );
}

export function markOtpRequired(userId: string) {
    if (!canUseStorage) {
        return;
    }

    window.sessionStorage.setItem(`${OTP_REQUIRED_KEY}:${userId}`, "1");
}

export function setOtpSession(
    userId: string,
    challengeId: string,
    correlationId: string,
    cooldownSeconds: number
) {
    if (!canUseStorage) {
        return;
    }

    const cooldownEnd = Date.now() + cooldownSeconds * 1000;
    window.sessionStorage.setItem(`${OTP_REQUIRED_KEY}:${userId}`, "1");
    window.sessionStorage.setItem(`${CHALLENGE_ID_KEY}:${userId}`, challengeId);
    window.sessionStorage.setItem(
        `${CORRELATION_ID_KEY}:${userId}`,
        correlationId
    );
    window.sessionStorage.setItem(
        `${COOLDOWN_END_KEY}:${userId}`,
        String(cooldownEnd)
    );

    ensureChannel();
    channel?.postMessage({
        type: "otp-challenge-set",
        userId,
        challengeId,
        correlationId,
        cooldownEnd,
    } satisfies OtpSessionMessage);
}

export function clearOtpSession(userId: string) {
    if (!canUseStorage) {
        return;
    }

    window.sessionStorage.removeItem(`${CHALLENGE_ID_KEY}:${userId}`);
    window.sessionStorage.removeItem(`${CORRELATION_ID_KEY}:${userId}`);
    window.sessionStorage.removeItem(`${COOLDOWN_END_KEY}:${userId}`);
    window.sessionStorage.removeItem(`${OTP_REQUIRED_KEY}:${userId}`);

    ensureChannel();
    channel?.postMessage({
        type: "otp-session-cleared",
        userId,
    } satisfies OtpSessionMessage);
}
